import { ShoppingCart } from '@mui/icons-material';
import { Dialog, DialogActions, Button } from '@mui/material';
import { useMemo } from 'react';

import { formatCommunityProductPrice } from '@jebel/utils';

import { useRequestCommunityProduct } from 'features/community-products/hooks';
import { CommunityProductFragment, CommunitySubscriptionFragment } from 'shared/graphql';
import { useCurrentUser, useMediaDetailer, useMediaPreview, useToast } from 'shared/hooks';

import {
  Content,
  ProductDescription,
  ProductLogo,
  ProductPrice,
  ProductTitle,
} from './CommunityProductPurchaseDetailsModal.styles';

interface Props {
  open: boolean;
  product: CommunityProductFragment | CommunitySubscriptionFragment;
  onClose: () => void;
}

export function CommunityProductPurchaseDetailsModal({ open, product, onClose }: Props) {
  const { data: image } = useMediaDetailer(product.image);
  const { src: logoSrc, srcSet: logoSrcSet } = useMediaPreview(product.image);
  const { userId } = useCurrentUser();
  const { showError, showSuccess } = useToast();
  const { mutateAsync: requestProduct, isLoading } = useRequestCommunityProduct();

  const priceText = useMemo(() => {
    return formatCommunityProductPrice(product);
  }, [product]);

  const handleBuy = async () => {
    if (!userId) {
      showError('You must be logged in to purchase a product');
      return;
    }

    if (!product.id) {
      showError('Product ID is missing');
      return;
    }

    const redirectUrl = window.location.href;

    try {
      const response = await requestProduct({
        user: userId,
        product: product.id,
        redirectUrl,
      });

      if (response.redirectTo) {
        // Redirect to Stripe Checkout for payment
        window.location.assign(response.redirectTo);
      } else {
        // Purchase completed without payment (one-time product)
        showSuccess('Product purchased successfully!');
        onClose();
      }
    } catch (error) {
      showError(
        error instanceof Error ? error.message : 'Failed to purchase product. Please try again.',
      );
    }
  };

  return (
    <Dialog maxWidth="xs" open={open} onClose={onClose} fullWidth>
      <Content dividers>
        <ProductLogo
          src={logoSrc}
          srcSet={logoSrcSet}
          color={product.membership?.color}
          alt={image?.filename ?? product.name}
        >
          <ShoppingCart fontSize="inherit" />
        </ProductLogo>

        <ProductTitle variant="h5">{product.name}</ProductTitle>

        {product.description && (
          <ProductDescription variant="body1">{product.description}</ProductDescription>
        )}

        <ProductPrice>{priceText}</ProductPrice>
      </Content>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>

        <Button
          variant="contained"
          color="primary"
          disabled={isLoading || !userId}
          startIcon={<ShoppingCart fontSize="small" />}
          onClick={handleBuy}
        >
          {isLoading ? 'Processing...' : 'Buy Now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
